import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { Buyer } from '@ps/core-models';
import {
  AdvancedFeasibilityFilters,
  FindSurveysParams,
  RespondentCountsParams,
  SearchCriteriaSurveysParams,
  SearchGroupSurveysParams,
  SurveyLocaleData,
  SurveyMetadataFormState,
  SurveyQualificationsFormState,
  SurveyQuotasFormState,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import {
  SurveyCreationPricingState,
  SurveyCreationInsightsActions,
  SurveyCreationInsightsPreDraftActions,
  AdvancedFilterActions,
} from './insights';

import { SurveyChildKey } from './survey-creation-shared-state.model';
import {
  SurveyCreationActions,
  SurveyCreationPreDraftActions,
} from './audience/survey-creation-audience.actions';
import { SurveyCreationReferenceActions } from './reference/survey-creation-reference.actions';

@Injectable({ providedIn: 'root' })
export class SurveyCreationActionsFacade {
  private readonly _store = inject(Store);

  public init(surveyId: Nullable<number>): void {
    this._store.dispatch(SurveyCreationActions.init({ surveyId }));
  }

  public buildPreDraftChildren(localeData: SurveyLocaleData[]): void {
    this._store.dispatch(
      SurveyCreationPreDraftActions.buildPreDraftChildren({ localeData }),
    );
  }

  public selectAllPreDraftChildren(): void {
    this._store.dispatch(
      SurveyCreationPreDraftActions.selectAllPreDraftChildren(),
    );
  }

  public clearAllPreDraftChildren(): void {
    this._store.dispatch(
      SurveyCreationPreDraftActions.clearAllPreDraftChildren(),
    );
  }

  public selectPreDraftChildren(ids: SurveyChildKey[]): void {
    this._store.dispatch(
      SurveyCreationPreDraftActions.selectPreDraftChildren({ ids }),
    );
  }

  public createStandardSurvey(): void {
    this._store.dispatch(SurveyCreationActions.createStandardSurvey());
  }

  public createSurveyAndDeleteDraft(deleteSurveyId: number): void {
    this._store.dispatch(
      SurveyCreationActions.createSurveyAndDeleteDraft({ deleteSurveyId }),
    );
  }

  public updateMetadata(metadata: SurveyMetadataFormState): void {
    this._store.dispatch(SurveyCreationActions.updateMetadata({ metadata }));
  }

  public updateQuotas(quotas: SurveyQuotasFormState): void {
    this._store.dispatch(SurveyCreationActions.updateQuotas({ quotas }));
  }

  public updateQualifications(
    qualifications: SurveyQualificationsFormState,
  ): void {
    this._store.dispatch(
      SurveyCreationActions.updateQualifications({ qualifications }),
    );
  }

  public saveSurvey(): void {
    this._store.dispatch(SurveyCreationActions.saveSurvey());
  }

  public createSurveyFromTemplate(
    templateId: number,
    deleteSurveyId: Nullable<number> = null,
  ): void {
    this._store.dispatch(
      SurveyCreationActions.createSurveyFromTemplate({
        templateId,
        deleteSurveyId,
      }),
    );
  }

  public clearSurveyId(): void {
    this._store.dispatch(SurveyCreationActions.clearSurveyId());
  }

  public saveAndCloneSurvey(): void {
    this._store.dispatch(SurveyCreationActions.saveAndCloneSurvey());
  }

  public saveAndCreateNew(): void {
    this._store.dispatch(SurveyCreationActions.saveAndCreateNew());
  }

  public saveAndLaunch(): void {
    this._store.dispatch(SurveyCreationActions.saveAndLaunch());
  }

  public saveAndEditAudience(): void {
    this._store.dispatch(SurveyCreationActions.saveAndEditAudience());
  }

  // Dispatch for SurveyCreationWorkflowExitEffects — leaving the survey
  // workflow, with or without saving, with or without an impersonation change.
  public saveAndExit(): void {
    this._store.dispatch(SurveyCreationActions.saveAndExit());
  }

  public discardSurveyChanges(): void {
    this._store.dispatch(SurveyCreationActions.discardSurveyChanges());
  }

  public saveSurveyAndDisconnectImpersonation(): void {
    this._store.dispatch(
      SurveyCreationActions.saveSurveyAndDisconnectImpersonation(),
    );
  }

  public saveSurveyAndSwitchImpersonatedBuyer(buyer: Buyer): void {
    this._store.dispatch(
      SurveyCreationActions.saveSurveyAndSwitchImpersonatedBuyer({ buyer }),
    );
  }

  public reset(): void {
    this._store.dispatch(SurveyCreationActions.reset());
  }

  public uploadLocationCodes(
    countryCode: string,
    qualificationCode: number,
    qualificationName: string,
    file: File,
  ): void {
    this._store.dispatch(
      SurveyCreationActions.uploadLocationCodes({
        countryCode,
        qualificationCode,
        qualificationName,
        file,
      }),
    );
  }

  public clearLocationCodes(
    qualificationCode: number,
    qualificationName: string,
  ): void {
    this._store.dispatch(
      SurveyCreationActions.clearLocationCodes({
        qualificationCode,
        qualificationName,
      }),
    );
  }

  public downloadLocationCodesTemplate(
    countryCode: string,
    qualificationCode: number,
  ): void {
    this._store.dispatch(
      SurveyCreationActions.downloadLocationCodesTemplate({
        countryCode,
        qualificationCode,
      }),
    );
  }

  public downloadValidatedLocationFile(qualificationCode: number): void {
    this._store.dispatch(
      SurveyCreationActions.downloadValidatedLocationFile({
        qualificationCode,
      }),
    );
  }

  public loadQualifications(localeData: SurveyLocaleData[]): void {
    for (const eachLocaleData of localeData) {
      this._store.dispatch(
        SurveyCreationReferenceActions.loadQualifications(eachLocaleData),
      );
    }
  }

  public loadQuestionCategories(): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadQuestionCategories(),
    );
  }

  public loadQuestions(
    search: string,
    categoryIds: number[],
    locale: string,
  ): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadQuestions({
        search,
        categoryIds,
        locale,
      }),
    );
  }

  public loadDependencyParentQuestion(
    parentQualificationId: number,
    locale: string,
  ): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadDependencyParentQuestion({
        parentQualificationId,
        locale,
      }),
    );
  }

  public loadLocationConditions(
    dbQualificationCode: number,
    locale: string,
  ): void {
    this._store.dispatch(
      SurveyCreationReferenceActions.loadLocationConditions({
        dbQualificationCode,
        locale,
      }),
    );
  }

  public toggleManualProcessing(companyId: number, enabled: boolean): void {
    this._store.dispatch(
      SurveyCreationActions.toggleManualProcessing({
        companyId,
        enabled,
      }),
    );
  }

  public applyCpiPricing(pricing: SurveyCreationPricingState): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.applyCpiPricing(pricing),
    );
  }

  public findSurveys(params: FindSurveysParams): void {
    this._store.dispatch(AdvancedFilterActions.findSurveys({ params }));
  }

  public getRespondentCounts(params: RespondentCountsParams): void {
    this._store.dispatch(AdvancedFilterActions.getRespondentCounts({ params }));
  }

  public searchCriteriaSurveys(params: SearchCriteriaSurveysParams): void {
    this._store.dispatch(
      AdvancedFilterActions.searchCriteriaSurveys({ params }),
    );
  }

  public searchGroupSurveys(params: SearchGroupSurveysParams): void {
    this._store.dispatch(AdvancedFilterActions.searchGroupSurveys({ params }));
  }

  public clearSurveyOptions(): void {
    this._store.dispatch(AdvancedFilterActions.clearSurveyOptions());
  }

  public applyAdvancedFilters(
    filters: AdvancedFeasibilityFilters,
    clearUploadedPsidsFor: Nullable<number>,
  ): void {
    this._store.dispatch(
      SurveyCreationActions.applyAdvancedFilters({
        filters,
        clearUploadedPsidsFor,
      }),
    );
  }

  public resetAdvancedFilters(): void {
    this._store.dispatch(AdvancedFilterActions.resetFilters());
  }

  public resyncSimilarSurveys(): void {
    this._store.dispatch(SurveyCreationInsightsActions.resyncSimilarSurveys());
  }

  public searchSimilarSurveysOutsideAccount(): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.searchSimilarSurveysOutsideAccount(),
    );
  }

  public cancelSimilarSurveysSearch(): void {
    this._store.dispatch(
      SurveyCreationInsightsActions.cancelSimilarSurveysSearch(),
    );
  }

  public calculateFeasibilityForPreDraft(): void {
    this._store.dispatch(
      SurveyCreationInsightsPreDraftActions.calculateFeasibilityForPreDraft(),
    );
  }
}
